import { useEffect, useState } from "react";
import { CreditCard, Plus, AlertTriangle, Loader2, CheckCircle2, Trash2, Info } from "lucide-react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Skeleton } from "@/components/common";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ISSUERS = ["HDFC Bank", "ICICI Bank", "SBI Card", "Axis Bank", "Kotak", "Amex", "Other"];

function DemoBanner() {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="cc-demo-banner">
      <AlertTriangle size={16} /> <span>Your card details are encrypted in transit and never stored by ZRo Pe.</span>
    </div>
  );
}

// Client-side card helpers. Full PAN / CVV are validated here and NEVER sent to the server.
function luhnValid(num) {
  const s = num.replace(/\D/g, "");
  if (s.length < 13) return false;
  let sum = 0, dbl = false;
  for (let i = s.length - 1; i >= 0; i--) {
    let d = parseInt(s[i], 10);
    if (dbl) { d *= 2; if (d > 9) d -= 9; }
    sum += d; dbl = !dbl;
  }
  return sum % 10 === 0;
}
function detectNetwork(num) {
  const s = num.replace(/\D/g, "");
  if (/^4/.test(s)) return "Visa";
  if (/^(5[1-5]|2[2-7])/.test(s)) return "Mastercard";
  if (/^3[47]/.test(s)) return "Amex";
  if (/^(60|65|81|82|508)/.test(s)) return "RuPay";
  if (/^3(0[0-5]|[68])/.test(s)) return "Diners Club";
  return "Other";
}
function formatCard(num, net) {
  const s = num.replace(/\D/g, "").slice(0, net === "Amex" ? 15 : 16);
  const groups = net === "Amex" ? [4, 6, 5] : [4, 4, 4, 4];
  const out = []; let i = 0;
  for (const g of groups) { if (i >= s.length) break; out.push(s.slice(i, i + g)); i += g; }
  return out.join(" ");
}

export default function CreditCards() {
  const [status, setStatus] = useState(null);    // credit_card module status
  const [statusMsg, setStatusMsg] = useState("");
  const [cards, setCards] = useState(null);
  const [history, setHistory] = useState([]);
  const [issuer, setIssuer] = useState("HDFC Bank");
  const [nickname, setNickname] = useState("");
  const [pan, setPan] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [holder, setHolder] = useState("");
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [payCard, setPayCard] = useState(null);
  const [amount, setAmount] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [useCoins, setUseCoins] = useState(false);
  const [useRewardCash, setUseRewardCash] = useState(false);
  const [coins, setCoins] = useState(null);
  const [paying, setPaying] = useState(false);

  const network = detectNetwork(pan);

  const load = () => {
    api.get("/features").then((r) => {
      const m = r.data?.modules?.credit_card || { status: "LIVE" };
      setStatus(m.status); setStatusMsg(m.message || "");
    }).catch(() => setStatus("LIVE"));
    api.get("/cards").then((r) => setCards(r.data)).catch((e) => { setCards([]); toast.error(apiError(e)); });
    api.get("/cards/payments").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/coins").then((r) => setCoins(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addCard = async (e) => {
    e.preventDefault(); setErr("");
    const digits = pan.replace(/\D/g, "");
    if (!luhnValid(digits)) { setErr("Please enter a valid card number."); return; }
    const m = expiry.match(/^(\d{2})\s*\/\s*(\d{2})$/);
    if (!m) { setErr("Enter expiry as MM/YY."); return; }
    const em = parseInt(m[1], 10), ey = 2000 + parseInt(m[2], 10);
    if (em < 1 || em > 12) { setErr("Invalid expiry month."); return; }
    const now = new Date(); const endOfMonth = new Date(ey, em, 1);
    if (endOfMonth <= now) { setErr("This card has expired."); return; }
    const cvvLen = network === "Amex" ? 4 : 3;
    if (!new RegExp(`^\\d{${cvvLen}}$`).test(cvv)) { setErr(`Enter a valid ${cvvLen}-digit CVV.`); return; }
    setAdding(true);
    try {
      // Send ONLY non-sensitive metadata. PAN & CVV never leave the browser.
      await api.post("/cards", {
        issuer, card_network: network, nickname, last4: digits.slice(-4),
        holder_name: holder, exp_month: em, exp_year: ey,
      });
      toast.success("Card added");
      setPan(""); setCvv(""); setExpiry(""); setHolder(""); setNickname("");  // clear sensitive fields
      load();
    } catch (err2) { toast.error(apiError(err2)); } finally { setAdding(false); }
  };

  const removeCard = async (id) => {
    try { await api.delete(`/cards/${id}`); toast.success("Card removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const doPay = async (outcome) => {
    setPaying(true);
    try {
      const { data } = await api.post("/cards/pay", { card_id: payCard.id, amount: parseFloat(amount), use_wallet: useWallet, use_coins: useCoins, use_reward_cash: useRewardCash, outcome });
      if (data.ok) {
        const coinMsg = data.coins_used > 0 ? ` ${data.coins_used.toLocaleString("en-IN")} Coins (₹${data.coins_value}) applied.` : "";
        const rcMsg = data.reward_cash_used > 0 ? ` ₹${data.reward_cash_used} Reward Cash applied.` : "";
        toast.success(`Payment confirmed. Reward ₹${data.reward_amount} credited.${coinMsg}${rcMsg}`);
      }
      else toast.error("Payment failed — no reward credited.");
      setPayCard(null); setAmount(""); setUseWallet(false); setUseCoins(false); setUseRewardCash(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setPaying(false); }
  };

  const est = amount ? Math.min(parseFloat(amount) * 0.01, 250).toFixed(2) : "0.00";

  if (cards === null || status === null) return <Skeleton className="h-96" />;

  if (status !== "LIVE") {
    return (
      <div className="space-y-5" data-testid="credit-cards-page">
        <div className="flex items-center gap-2">
          <CreditCard size={22} className="text-[#14B8A6]" />
          <h1 className="font-display text-2xl font-bold text-[#0B132B]">Credit Card Bill Payment</h1>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="cc-unavailable">
          {status === "MAINTENANCE" ? (
            // Under Maintenance artwork — always shown here, independent of the cosmetic penguin toggle
            <img
              src="/brand/penguin-maintenance-2.png"
              alt="ZRo Pe penguin working — we'll be back soon"
              loading="eager"
              decoding="async"
              data-testid="maintenance-penguin-image"
              className="mx-auto h-auto w-[clamp(200px,55vw,400px)] max-h-[42vh] object-contain"
            />
          ) : (
            <AlertTriangle size={40} className="mx-auto text-amber-500" />
          )}
          <h3 className="mt-3 font-display text-lg font-bold text-[#0B132B]">
            {status === "MAINTENANCE" ? "Under maintenance" : "Currently unavailable"}
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
            {statusMsg || "The Credit Card module is not available right now. Please check back later."}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="credit-cards-page">
      <div className="flex items-center gap-2">
        <CreditCard size={22} className="text-[#14B8A6]" />
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">Credit Card Bill Payment</h1>
      </div>
      <DemoBanner />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-display font-bold text-[#0B132B]">Add a card</h3>
          <form onSubmit={addCard} className="mt-3 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Issuer / Bank</label>
              <Select value={issuer} onValueChange={setIssuer}>
                <SelectTrigger className="mt-1 bg-white text-[#0B132B]" data-testid="cc-issuer"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white text-[#0B132B]">{ISSUERS.map((i) => <SelectItem key={i} value={i} className="text-[#0B132B] focus:bg-slate-100 focus:text-[#0B132B] data-[state=checked]:font-semibold">{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Card number</label>
              <div className="relative mt-1">
                <input data-testid="cc-number" inputMode="numeric" autoComplete="cc-number" value={formatCard(pan, network)}
                  onChange={(e) => setPan(e.target.value)} placeholder="1234 5678 9012 3456"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-16 text-sm tracking-wide outline-none focus:border-[#14B8A6]" />
                {pan.replace(/\D/g, "").length >= 2 && (
                  <span data-testid="cc-network-badge" className="absolute right-2 top-1/2 -translate-y-1/2 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{network}</span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-semibold text-slate-600">Expiry (MM/YY)</label>
                <input data-testid="cc-expiry" inputMode="numeric" autoComplete="cc-exp" maxLength={5} value={expiry}
                  onChange={(e) => { let v = e.target.value.replace(/\D/g, "").slice(0, 4); if (v.length >= 3) v = v.slice(0, 2) + "/" + v.slice(2); setExpiry(v); }}
                  placeholder="MM/YY" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
              </div>
              <div className="w-28">
                <label className="text-xs font-semibold text-slate-600">CVV</label>
                <input data-testid="cc-cvv" inputMode="numeric" autoComplete="off" maxLength={4} value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder={network === "Amex" ? "4 digits" : "3 digits"}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Name on card</label>
              <input data-testid="cc-holder" autoComplete="cc-name" value={holder} onChange={(e) => setHolder(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Card nickname (optional)</label>
              <input data-testid="cc-nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. Travel card"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
            </div>
            <p className="flex items-start gap-1.5 text-[11px] text-slate-400"><Info size={12} className="mt-0.5 shrink-0" /> Your card number and CVV are used only to add the card securely and are never stored by ZRo Pe — we keep only the network, expiry and last 4 digits.</p>
            {err && <p className="text-xs font-semibold text-red-600" data-testid="cc-add-error">{err}</p>}
            <button data-testid="cc-add-submit" disabled={adding} className="flex items-center gap-2 rounded-lg bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f9c8c] disabled:opacity-60">
              {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add Card
            </button>
          </form>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-display font-bold text-[#0B132B]">Your cards</h3>
          <div className="mt-3 space-y-2">
            {cards.length === 0 && <p className="text-sm text-slate-400">No cards yet. Add one to pay a bill.</p>}
            {cards.map((c) => (
              <div key={c.id} className="rounded-lg border border-slate-100 px-4 py-3" data-testid={`cc-row-${c.last4}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#0B132B]">{c.nickname || c.issuer}</p>
                    <p className="text-xs text-slate-500">{c.issuer}{c.card_network ? ` · ${c.card_network}` : ""} · {c.masked} · <span className="text-emerald-600">{c.status}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPayCard(c)} data-testid={`cc-pay-${c.last4}`}
                      className="rounded-lg bg-[#0B132B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0B132B]/90">Pay Bill</button>
                    <button onClick={() => removeCard(c.id)} data-testid={`cc-remove-${c.last4}`}
                      className="rounded-lg border border-slate-200 px-2 py-1.5 text-slate-400 hover:border-red-200 hover:text-red-600" title="Remove card"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1.5 rounded-md bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500" data-testid={`cc-bill-${c.last4}`}>
                  <Info size={12} /> Bill details not synced yet — connect a supported bill source later to auto-fetch outstanding &amp; due dates.
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {payCard && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => !paying && setPayCard(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cc-pay-modal">
            <h3 className="font-display text-lg font-bold text-[#0B132B]">Pay {payCard.issuer} {payCard.masked}</h3>
            <div className="mt-2"><DemoBanner /></div>
            <div className="mt-3 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Bill amount (₹)</label>
                <input data-testid="cc-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} data-testid="cc-use-wallet" className="accent-[#14B8A6]" />
                Use ZRo Pe Wallet balance
              </label>
              {coins && coins.balance >= 1000 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <label className="flex items-center gap-2 text-sm text-[#0B132B]">
                    <input type="checkbox" checked={useCoins} onChange={(e) => setUseCoins(e.target.checked)} data-testid="cc-use-coins" className="accent-[#F5B301]" />
                    <img src="/brand/zrope-coin.png" alt="" className="h-5 w-5 rounded-full object-cover" />
                    Use ZRo Pe Coins <span className="text-slate-500">({coins.balance.toLocaleString("en-IN")} • up to ₹{coins.rupee_value})</span>
                  </label>
                  <p className="mt-1 text-[11px] italic text-slate-500" data-testid="cc-coins-note">ZRo Pe Coins are non-withdrawable reward credits and can only be used toward eligible payments.</p>
                </div>
              ) : null}
              {coins && (coins.reward_rupees || 0) > 0 ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <label className="flex items-center gap-2 text-sm text-[#0B132B]">
                    <input type="checkbox" checked={useRewardCash} onChange={(e) => setUseRewardCash(e.target.checked)} data-testid="cc-use-reward-cash" className="accent-emerald-600" />
                    Use Reward Cash <span className="text-slate-500">(₹{coins.reward_rupees} available)</span>
                  </label>
                  <p className="mt-1 text-[11px] italic text-slate-500" data-testid="cc-reward-cash-note">Reward Cash is a non-withdrawable reward and can only be used toward eligible payments.</p>
                </div>
              ) : null}
              <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">Estimated reward: <b>₹{est}</b> (credited only after payment is confirmed)</div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPayCard(null)} disabled={paying} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={() => doPay("failed")} disabled={paying} data-testid="cc-pay-fail" className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">Simulate Fail</button>
              <button onClick={() => doPay("success")} disabled={paying} data-testid="cc-pay-confirm" className="flex items-center gap-2 rounded-lg bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f9c8c] disabled:opacity-60">
                {paying ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Pay
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display font-bold text-[#0B132B]">Credit Card payment history</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Card</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Reward</th><th className="py-2 pr-4">Status</th><th className="py-2">Txn</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No credit card payments yet.</td></tr>}
              {history.map((p) => (
                <tr key={p.txn_id} data-testid={`cc-hist-${p.txn_id}`}>
                  <td className="py-2 pr-4 text-slate-500">{fmtDateTime(p.created_at)}</td>
                  <td className="py-2 pr-4">{p.issuer} •••• {p.last4}</td>
                  <td className="py-2 pr-4">₹{p.final_amount}</td>
                  <td className="py-2 pr-4">₹{p.reward_amount || 0}</td>
                  <td className="py-2 pr-4"><span className={p.card_state === "FAILED" ? "text-red-600" : "text-emerald-600"}>{p.card_state}</span></td>
                  <td className="py-2 text-slate-400">{p.txn_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
